import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Zap, ShoppingCart, Timer, Loader2, PackageCheck, PackageX } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";

const useCountdown = (endsAt) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!endsAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  if (!endsAt) return null;
  const diff = Math.max(0, new Date(endsAt).getTime() - now);
  const d = Math.floor(diff / 86400000), h = Math.floor(diff / 3600000) % 24;
  const m = Math.floor(diff / 60000) % 60, s = Math.floor(diff / 1000) % 60;
  return { expired: diff === 0, label: `${d}j ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` };
};

export const OfferVisual = ({ offer, countdown, children }) => {
  const soldOut = (offer.stock ?? 0) <= 0;
  const discount = offer.old_price && offer.old_price > offer.price ? Math.round((1 - offer.price / offer.old_price) * 100) : null;
  return (
    <div className="metal-border rounded-2xl overflow-hidden grid sm:grid-cols-[240px_1fr] h-full" data-testid={`flash-offer-card-${offer.id || "preview"}`}>
      <div style={{ background: "#fff" }} className="relative h-52 sm:h-full min-h-[200px]">
        {offer.image_path
          ? <img src={fileUrl(offer.image_path)} alt={offer.title} className="absolute inset-0 h-full w-full object-contain p-3" data-testid="flash-offer-image" />
          : <div className="absolute inset-0 flex items-center justify-center"><img src="/logo.png" alt="" className="h-32 opacity-80" /></div>}
        {soldOut && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><span className="px-4 py-2 rounded-lg bg-slate-900 text-white font-heading font-black uppercase tracking-wider -rotate-6" data-testid="flash-offer-soldout">Épuisé</span></div>}
        {discount && <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-red-600 text-white text-sm font-black" data-testid="flash-offer-discount">-{discount}%</span>}
      </div>
      <div className="p-5 sm:p-6 flex flex-col">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-600 text-white text-xs font-black uppercase tracking-wider flash-pulse">
            <Zap className="h-3.5 w-3.5" />Offre flash du mois
          </span>
          {countdown && (
            <span className="inline-flex items-center gap-1.5 text-sm font-mono text-amber-600 font-bold" data-testid="flash-offer-countdown">
              <Timer className="h-4 w-4" />{countdown.expired ? "Offre expirée" : countdown.label}
            </span>
          )}
        </div>
        <h3 className="font-heading font-extrabold text-2xl mt-3" data-testid="flash-offer-title">{offer.title}</h3>
        {offer.description && <p className="text-muted-foreground text-sm mt-1 whitespace-pre-line">{offer.description}</p>}
        <div className="flex items-end gap-3 mt-4">
          <span className="font-heading font-black text-4xl text-brand-red" data-testid="flash-offer-price">{Number(offer.price).toFixed(2)} €</span>
          {offer.old_price ? <span className="text-muted-foreground line-through mb-1" data-testid="flash-offer-old-price">{Number(offer.old_price).toFixed(2)} €</span> : null}
          <span className="text-xs text-muted-foreground mb-1.5">TTC</span>
        </div>
        <div className={`mt-2 inline-flex items-center gap-1.5 text-sm font-semibold ${soldOut ? "text-slate-500" : (offer.stock <= 5 ? "text-red-600" : "text-emerald-600")}`} data-testid="flash-offer-stock">
          {soldOut ? <><PackageX className="h-4 w-4" />Épuisé</> : <><PackageCheck className="h-4 w-4" />{offer.stock <= 5 ? `Plus que ${offer.stock} en stock` : `${offer.stock} en stock`}</>}
        </div>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
};

const OrderDialog = ({ offer, open, onOpenChange, onOrdered }) => {
  const [qty, setQty] = useState(1);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async () => {
    setSending(true);
    try {
      await api.post(`/offers/${offer.id}/order`, { quantity: Number(qty) || 1, message });
      onOrdered?.();
      toast.success("Commande envoyée au fournisseur");
      onOpenChange(false);
      setQty(1); setMessage("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="order-dialog" className="max-w-md" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Commander : {offer.title}</DialogTitle></DialogHeader>
        <div><Label>Quantité</Label><Input data-testid="order-qty" type="number" min={1} max={offer.stock} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1.5" /></div>
        <div><Label>Message (optionnel)</Label><Textarea data-testid="order-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Livraison, référence véhicule..." className="mt-1.5" /></div>
        <div className="flex justify-between items-center text-sm"><span className="text-muted-foreground">Total</span>
          <span className="font-heading font-black text-xl text-brand-red" data-testid="order-total">{(offer.price * (Number(qty) || 1)).toFixed(2)} €</span></div>
        <Button data-testid="order-submit-btn" onClick={submit} disabled={sending} className="w-full bg-blue-700 hover:bg-blue-800 h-11">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShoppingCart className="h-4 w-4 mr-2" />Confirmer la commande</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const OfferSlide = ({ offer, onOrdered }) => {
  const [open, setOpen] = useState(false);
  const countdown = useCountdown(offer.ends_at);
  const soldOut = (offer.stock ?? 0) <= 0;
  return (
    <>
      <OfferVisual offer={offer} countdown={countdown}>
        <Button data-testid={`flash-offer-order-btn-${offer.id}`} onClick={() => setOpen(true)} disabled={soldOut || countdown?.expired}
          className="w-full sm:w-auto px-8 h-12 text-base font-bold bg-blue-700 hover:bg-blue-800">
          <ShoppingCart className="h-5 w-5 mr-2" />{soldOut ? "Épuisé" : "Commander"}
        </Button>
      </OfferVisual>
      <OrderDialog offer={offer} open={open} onOpenChange={setOpen} onOrdered={onOrdered} />
    </>
  );
};

export default function FlashOffers() {
  const [offers, setOffers] = useState([]);
  const load = () => api.get("/offers").then(({ data }) => setOffers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!offers.length) return null;
  return (
    <motion.section id="flash-offers" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8 scroll-mt-24" data-testid="flash-offer-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-extrabold text-lg flex items-center gap-2"><Zap className="h-5 w-5 text-red-600" />Offres flash <span className="text-sm font-semibold text-muted-foreground">({offers.length})</span></h2>
      </div>
      <Carousel opts={{ align: "start", loop: offers.length > 1 }} className="relative" data-testid="flash-offers-carousel">
        <CarouselContent>
          {offers.map((o) => (
            <CarouselItem key={o.id} className="basis-full">
              <OfferSlide offer={o} onOrdered={load} />
            </CarouselItem>
          ))}
        </CarouselContent>
        {offers.length > 1 && (
          <>
            <CarouselPrevious data-testid="flash-offers-prev" className="left-auto right-14 top-3 translate-y-0 sm:left-[-1rem] sm:right-auto sm:top-1/2 sm:-translate-y-1/2 bg-white" />
            <CarouselNext data-testid="flash-offers-next" className="right-3 top-3 translate-y-0 sm:right-[-1rem] sm:top-1/2 sm:-translate-y-1/2 bg-white" />
          </>
        )}
      </Carousel>
    </motion.section>
  );
}
